import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { initials, fmtTime, SESSION_STATUS_STYLES } from "@/lib/helpers";
import { ArrowLeft, Play, Pause, StopCircle, FloppyDisk, Star, Paperclip } from "@phosphor-icons/react";
import AssessmentTemplateForm from "@/components/AssessmentTemplateForm";
import { FileAttachments } from "@/components/ui/file-attachments";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";

import { devLog } from "@/lib/devLog";
function fmtElapsed(secs) {
  const s = Math.max(0, Math.floor(secs));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2,"0")}:${String(sec).padStart(2,"0")}`
    : `${String(m).padStart(2,"0")}:${String(sec).padStart(2,"0")}`;
}

function computeElapsed(session) {
  if (!session?.started_at) return 0;
  const startMs = new Date(session.started_at).getTime();
  const nowMs = Date.now();
  const paused = (session.paused_seconds || 0) * 1000;
  let currentPaused = 0;
  if (session.status === "paused" && session.paused_at) {
    currentPaused = nowMs - new Date(session.paused_at).getTime();
  }
  const ended = session.ended_at ? new Date(session.ended_at).getTime() : nowMs;
  return Math.floor((ended - startMs - paused - currentPaused) / 1000);
}

export default function SessionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { can } = usePermissions();
  const [session, setSession] = useState(null);
  const [notes, setNotes] = useState("");
  const [showAssess, setShowAssess] = useState(false);
  const [rating, setRating] = useState(4);
  const [goal, setGoal] = useState("");
  const [nextFocus, setNextFocus] = useState("");
  const [assessNotes, setAssessNotes] = useState("");
  const [template, setTemplate] = useState(null);
  const [customFields, setCustomFields] = useState({});
  const [tick, setTick] = useState(0);
  // Individual completion panel for one student in a group session.
  const [studentPanel, setStudentPanel] = useState(null);
  const [studentNotes, setStudentNotes] = useState("");
  const [finishing, setFinishing] = useState(false);

  // iter-67j (Area 1e) — Frontend ownership gate: hide notes save +
  // file attach controls when the caller is NOT the assigned
  // therapist. Owner / admin bypass. Backend enforces the same rule.
  const isOwnerAdmin = (user?.roles || []).some(r => r === "owner" || r === "admin");
  const canEditThisSession = isOwnerAdmin || (session && session.therapist_id === user?.id);

  const load = async () => {
    try {
      const { data } = await api.get(`/sessions/${id}`);
      setSession(data);
      setNotes(data.notes || "");
      // Load assessment template for this therapy type
      if (data.therapy_type_id) {
        try {
          const t = await api.get(`/assessment-templates/by-therapy/${data.therapy_type_id}`);
          setTemplate(t.data || null);
        } catch (err) { devLog("template fetch failed", err); }
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  useEffect(() => {
    if (!session || session.status !== "in_progress") return;
    const t = setInterval(() => setTick(x => x + 1), 1000);
    return () => clearInterval(t);
  }, [session?.status, session?.started_at]);

  const doAction = async (path) => {
    try {
      await api.post(`/sessions/${id}/${path}`);
      toast.success(path.replace("-", " "));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveNotes = async () => {
    try {
      await api.post(`/sessions/${id}/notes`, { notes });
      toast.success("Notes saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const completeWithAssessment = async () => {
    try {
      await api.post(`/sessions/${id}/complete`, {
        rating, goal_progress: goal, notes: assessNotes, next_focus: nextFocus,
        custom_fields: customFields,
      });
      toast.success("Session completed");
      setShowAssess(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openStudentPanel = (student) => {
    setStudentNotes("");
    setStudentPanel(student);
  };

  // Finishes ONE student: their notes, attachments and end time go onto
  // their own session record; everyone else keeps going.
  const completeStudent = async () => {
    setFinishing(true);
    try {
      const { data } = await api.post(`/sessions/${id}/complete-student`, {
        student_id: studentPanel.id,
        notes: studentNotes,
      });
      toast.success(data.group_completed
        ? `${studentPanel.name} completed — that was the last student, so the session is finished`
        : `${studentPanel.name} completed · ${data.remaining_students} still in session`);
      setStudentPanel(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setFinishing(false);
    }
  };

  if (!session) return <div className="text-sm text-muted-foreground">Loading session…</div>;

  const elapsed = computeElapsed(session);
  const tt = session.therapy_type;
  const canControl = session.status !== "completed";
  const isLive = session.status === "in_progress" || session.status === "paused";
  // Persistent roster log (student, entry, exit). Falls back to the live
  // student list for sessions created before the log existed.
  const activeById = Object.fromEntries((session.students || []).map(s => [s.id, s]));
  const rosterRows = (session.roster_log || []).length
    ? session.roster_log
    : (session.students || []).map(s => ({
        student_id: s.id, name: s.name,
        entered_at: session.started_at,
        exited_at: session.status === "completed" ? session.ended_at : null,
      }));

  return (
    <div className="space-y-6 max-w-5xl">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-1.5 -ml-2"><ArrowLeft size={14} /> Back</Button>

      {/* Live timer hero */}
      <Card className="overflow-hidden border-primary/20">
        <div className="p-6 md:p-8 relative" style={{ backgroundColor: (tt?.color || "#4A6753") + "12" }}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="overline mb-1.5" style={{ color: tt?.color }}>{tt?.name || "Session"}</div>
              <div className="flex items-center gap-3">
                <div className="font-display text-5xl md:text-6xl font-bold tracking-tight tabular-nums">
                  {fmtElapsed(elapsed)}
                </div>
                {session.status === "in_progress" && (
                  <span className="relative inline-flex"><span className="h-3 w-3 rounded-full bg-teal-500 pulse-dot relative" /></span>
                )}
              </div>
              <div className="mt-2">
                <Badge className={SESSION_STATUS_STYLES[session.status]}>{session.status.replace("_", " ")}</Badge>
              </div>
            </div>

            {canControl && (
              <div className="flex flex-wrap gap-2">
                {session.status === "scheduled" && (
                  <Button onClick={() => doAction("start")} className="gap-1.5" data-testid="session-start"><Play size={14} weight="fill" /> Start</Button>
                )}
                {session.status === "in_progress" && (
                  <>
                    <Button variant="outline" onClick={() => doAction("pause")} className="gap-1.5" data-testid="session-pause"><Pause size={14} /> Pause</Button>
                    <Button onClick={() => setShowAssess(true)} className="gap-1.5" data-testid="session-complete"><StopCircle size={14} weight="fill" /> Complete</Button>
                  </>
                )}
                {session.status === "paused" && (
                  <>
                    <Button onClick={() => doAction("start")} className="gap-1.5"><Play size={14} weight="fill" /> Resume</Button>
                    <Button variant="outline" onClick={() => setShowAssess(true)} className="gap-1.5">Complete</Button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Info */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="md:col-span-2">
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle className="text-base font-display tracking-tight">
              Students in session
              {rosterRows.length > 1 && (
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  · {rosterRows.length} students
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {rosterRows.map(row => {
              const active = activeById[row.student_id];
              const canFinish = isLive && canEditThisSession && Boolean(active);
              return (
                <div key={row.student_id} className="flex items-center gap-3 p-2 rounded hover:bg-muted/40" data-testid={`session-student-${row.student_id}`}>
                  <Avatar className="h-9 w-9"><AvatarFallback className="bg-primary/10 text-primary font-semibold">{initials(row.name)}</AvatarFallback></Avatar>
                  <div className="flex-1 min-w-0">
                    {canFinish ? (
                      <button
                        type="button"
                        onClick={() => openStudentPanel(active)}
                        className="text-sm font-medium text-primary hover:underline text-left truncate"
                        data-testid={`student-link-${row.student_id}`}
                      >
                        {row.name}
                      </button>
                    ) : (
                      <div className="text-sm font-medium truncate">{row.name}</div>
                    )}
                    {/* Persistent entry / exit log — survives individual
                        completion so the record stays on the page. */}
                    <div className="text-xs text-muted-foreground truncate" data-testid={`student-times-${row.student_id}`}>
                      In {row.entered_at ? fmtTime(row.entered_at) : "—"}
                      {" · "}
                      {row.exited_at
                        ? <>Out {fmtTime(row.exited_at)}</>
                        : <span className="text-teal-700 font-medium">In session</span>}
                    </div>
                  </div>
                  {canFinish && (
                    <span className="text-[11px] text-muted-foreground whitespace-nowrap">Notes &amp; finish</span>
                  )}
                </div>
              );
            })}
            {rosterRows.length === 0 && (
              <div className="text-xs text-muted-foreground">No students recorded on this session.</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base font-display tracking-tight">Therapist</CardTitle></CardHeader>
          <CardContent>
            {session.therapist && (
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10"><AvatarFallback className="bg-accent/20 text-accent font-semibold">{initials(session.therapist.name)}</AvatarFallback></Avatar>
                <div><div className="text-sm font-medium">{session.therapist.name}</div><div className="text-xs text-muted-foreground">{session.therapist.email}</div></div>
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground space-y-1">
              <div>Started: <span className="text-foreground">{fmtTime(session.started_at)}</span></div>
              {session.ended_at && <div>Ended: <span className="text-foreground">{fmtTime(session.ended_at)}</span></div>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Notes */}
      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-base font-display tracking-tight">Session notes</CardTitle>
          {canEditThisSession && (
            <Button size="sm" variant="outline" onClick={saveNotes} className="gap-1.5" data-testid="save-session-notes"><FloppyDisk size={14} /> Save</Button>
          )}
        </CardHeader>
        <CardContent>
          <Textarea
            value={notes}
            onChange={(e)=>setNotes(e.target.value)}
            rows={5}
            placeholder={canEditThisSession
              ? "What was covered today? Progress, challenges, home practice…"
              : "Notes are read-only for sessions assigned to another therapist."
            }
            readOnly={!canEditThisSession}
            data-testid="session-notes-textarea"
          />
        </CardContent>
      </Card>

      {/* Attachments — photos, worksheets, home-practice PDFs, etc. */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-display tracking-tight flex items-center gap-2">
            <Paperclip size={16} weight="duotone" /> Attachments
          </CardTitle>
          <p className="text-xs text-muted-foreground">Photos or PDFs from today&apos;s session (worksheets, progress charts, home-practice sheets). Images ≤10 MB, PDFs ≤25 MB.</p>
        </CardHeader>
        <CardContent>
          <FileAttachments entityType="session" entityId={id} />
        </CardContent>
      </Card>

      {/* Assessment (if completed) */}
      {session.assessment && (
        <Card>
          <CardHeader><CardTitle className="text-base font-display tracking-tight">Assessment</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex items-center gap-1">
              {[1,2,3,4,5].map(n => <Star key={n} size={18} weight={n <= session.assessment.rating ? "fill" : "regular"} className={n <= session.assessment.rating ? "text-amber-500" : "text-muted-foreground"} />)}
              <span className="text-xs text-muted-foreground ml-2">Rating: {session.assessment.rating}/5</span>
            </div>
            {session.assessment.goal_progress && <div><span className="overline">Goal progress</span><div>{session.assessment.goal_progress}</div></div>}
            {session.assessment.notes && <div><span className="overline">Notes</span><div className="whitespace-pre-line">{session.assessment.notes}</div></div>}
            {session.assessment.next_focus && <div><span className="overline">Next focus</span><div>{session.assessment.next_focus}</div></div>}
            {session.assessment.custom_fields && Object.keys(session.assessment.custom_fields).length > 0 && template && (
              <div className="pt-2 border-t border-border">
                <span className="overline">{template.name}</span>
                <div className="grid md:grid-cols-2 gap-3 mt-2">
                  {(template.fields || []).map((f) => {
                    const v = session.assessment.custom_fields[f.key];
                    if (v === undefined || v === "" || (Array.isArray(v) && v.length === 0)) return null;
                    return (
                      <div key={f.key} className="text-xs">
                        <div className="text-muted-foreground">{f.label}</div>
                        <div className="text-foreground">
                          {f.type === "rating" && `${v} / 5`}
                          {f.type !== "rating" && (Array.isArray(v) ? v.join(", ") : String(v))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Complete dialog */}
      <Dialog open={showAssess} onOpenChange={setShowAssess}>
        <DialogContent className="bg-card max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              Complete session {template ? `· ${template.name}` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Overall rating</Label>
              <div className="flex gap-1">
                {[1,2,3,4,5].map(n => (
                  <button key={n} type="button" onClick={() => setRating(n)} data-testid={`rating-${n}`}>
                    <Star size={28} weight={n <= rating ? "fill" : "regular"} className={n <= rating ? "text-amber-500" : "text-muted-foreground"} />
                  </button>
                ))}
              </div>
            </div>

            {template && (
              <div className="pt-2 border-t border-border">
                <div className="overline mb-2">{template.name}</div>
                <AssessmentTemplateForm template={template} value={customFields} onChange={setCustomFields} />
              </div>
            )}

            <div className="pt-2 border-t border-border space-y-3">
              <div className="space-y-1.5">
                <Label>Goal progress</Label>
                <Textarea rows={2} value={goal} onChange={e=>setGoal(e.target.value)} placeholder="Improved eye contact, spoke 3-word sentences…" />
              </div>
              <div className="space-y-1.5">
                <Label>General notes</Label>
                <Textarea rows={3} value={assessNotes} onChange={e=>setAssessNotes(e.target.value)} placeholder="Detailed observations…" />
              </div>
              <div className="space-y-1.5">
                <Label>Next focus</Label>
                <Textarea rows={2} value={nextFocus} onChange={e=>setNextFocus(e.target.value)} placeholder="What to work on next session" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssess(false)}>Cancel</Button>
            <Button onClick={completeWithAssessment} data-testid="confirm-complete">Complete session</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Individual student panel — notes, attachments and finishing just
          this student out of a group session. */}
      <Dialog open={!!studentPanel} onOpenChange={(o) => !o && setStudentPanel(null)}>
        <DialogContent className="bg-card max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {studentPanel?.name}
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              Notes and files saved here belong to {studentPanel?.name} alone.
              Completing finishes only their session — everyone else carries on.
            </p>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Session notes for {studentPanel?.name}</Label>
              <Textarea
                rows={5}
                value={studentNotes}
                onChange={e => setStudentNotes(e.target.value)}
                placeholder="What was covered with this student today?"
                data-testid="student-notes-textarea"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5">
                <Paperclip size={14} weight="duotone" /> Attachments for {studentPanel?.name}
              </Label>
              {studentPanel && (
                <FileAttachments
                  entityType="session_student"
                  entityId={`${id}::${studentPanel.id}`}
                />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStudentPanel(null)} data-testid="student-panel-cancel">
              Cancel
            </Button>
            <Button
              onClick={completeStudent}
              disabled={finishing}
              className="gap-1.5"
              data-testid="student-panel-complete"
            >
              <StopCircle size={14} weight="fill" />
              {finishing ? "Completing…" : "Complete student"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
